import {startCase} from 'lodash';
import {
  isFormRadioInput,
  isFormRadioWrapper,
  isFormInputElement,
  isFormLabel,
  isFormFileUploadLabel,
  isFormForm,
} from '@packages/utilities/elementTypeUtils';
import {getBody} from '../elementGetters';
import {
  getChildren,
  findElement,
  getPrev,
  getNext,
  pageComponentType,
} from '@packages/utilities/core-elements';
import {updateComponentRenderInComponentMap} from '../ComponentMapUtils';
import {
  Expressions,
  Component,
  type ExpressionPath,
  matchExpression,
} from '@packages/systems/wfdl';
import {None, Some, type Option} from '@packages/utilities/fp/option';
import {
  type Expression,
  type ExpressionElement,
  EText,
  EList,
  EElement,
  EUnion,
  ERecord,
} from '@packages/systems/wfdl-ast';
import {
  emptyObject,
  emptyArray,
  assoc,
  identity,
} from '@packages/utilities/fp/utils';
import {has} from '@packages/utilities/key-map';
import {
  trimLastNum,
  weakEqual as weakStringEqual,
} from '@packages/utilities/string-utils';
import {getNewElementId} from '../NamedElementUtils';
import {type Qualified} from '@packages/systems/wfdl-Qualified';

/**
 * Traverses the DesignerState tree and returns a unique form ID and human readable label
 * @param {Record} components - a component map
 * @param {String} [prefix='element'] - Prefix for unique ID
 * @returns Object
 */
export const getUniqueFormIdAndLabel = (
  components: Component.ComponentMap,
  prefix: string
): {
  id: string;
  label: string;
} => {
  const newPrefix = getNewElementId(components, prefix);
  return {
    id: newPrefix,
    label: startCase(newPrefix),
  };
};
// TODO: Migrate these utilities to not return Options, instead take an element and return an element where possible
export const findRadioInput = (
  element: ExpressionElement
): Option<ExpressionElement> => {
  const radioInput = findElement(element, isFormRadioInput);
  return radioInput ? Some(radioInput) : None;
};

const keepRadio = (element: ExpressionElement): Option<ExpressionElement> => {
  return isFormRadioWrapper(element) ? Some(element) : None;
};

const getSurroundingRadio =
  (formRadioWrapperId: string) =>
  (root: Expression): Option<ExpressionElement> => {
    return getPrev(formRadioWrapperId)(root)
      .chain(keepRadio)
      .alt(getNext(formRadioWrapperId)(root).chain(keepRadio));
  };

const getInputName = (element: ExpressionElement) => {
  return {
    attrName: Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'attr'},
        {in: 'Record', at: 'name'},
      ],
      'Text',
      'default'
    ),
    formName: Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'form'},
        {in: 'Record', at: 'name'},
      ],
      'Text',
      'default'
    ),
  };
};

type RadioName = {
  attrName: string;
  formName: string;
};

/*
 * HOW IT WORKS.
 * When a new radio input is added to a form
 * `getSurroundingRadioName` is called and looks for other radio inputs in the form.
 * First it looks to the previous sibling, and if it exists and is another FormRadioWrapper, it will take the
 * formName, and apply it to the new radio input's group name. (Yes, how convenient for the user!)
 * If it can't find a radioInput as a previous sibling, then it will look at the next sibling for the same.
 * It will return None if it can't find another FormWrapper as an immediate sibling.
 */
export const getSurroundingRadioName =
  (formRadioWrapperId: string): ((root: Expression) => Option<RadioName>) =>
  (root: Expression): Option<RadioName> =>
    getSurroundingRadio(formRadioWrapperId)(root)
      .chain(findRadioInput)
      .map(getInputName);

/**
 * A convenience utility to generate a path for values in nested Record types.
 * In the future we can consider a more generalized solution that will allow safely accessing
 * different data types. Please use with care. See this thread about the concerns of misusing this function:
 * https://webflow.slack.com/archives/C02155UPC7L/p1641301238153600?thread_ts=1641221893.137000&cid=C02155UPC7L
 */
export const generateRecordPath = (pathString: string): ExpressionPath => {
  return pathString.split('.').map((segment) => ({in: 'Record', at: segment}));
};

export const ATTR_FOR_PATH: ExpressionPath = generateRecordPath('attr.for');
export const FORM_NAME_PATH: ExpressionPath = generateRecordPath('form.name');
export const ATTR_NAME_PATH: ExpressionPath = generateRecordPath('attr.name');
const ATTR_ID_PATH = generateRecordPath('attr.id');
export const ATTR_DATA_NAME_PATH: ExpressionPath =
  generateRecordPath('attr.data-name');
const CHILDREN_PATH = generateRecordPath('children');
const SOURCE_PATH = generateRecordPath('Source');

type NewIdMapType = {
  [key: string]: {
    id: string;
    label: string;
  };
};

type FormInputAttributes = {
  formName?: string;
  dataName?: string;
  name?: string;
};

const keyToDataPath = {
  name: ATTR_NAME_PATH,
  formName: FORM_NAME_PATH,
  dataName: ATTR_DATA_NAME_PATH,
} as const;

export const setFormInputAttributes = (
  element: ExpressionElement,
  values: FormInputAttributes
): ExpressionElement => {
  let data = Expressions.getElementData(element);

  // Iterate over the values provided and put them in the correct path on the
  // data object.
  Object.entries(values).forEach(([key, val]: [any, any]) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly name: ExpressionPath; readonly formName: ExpressionPath; readonly dataName: ExpressionPath; }'.
    if (val) data = Expressions.setIn(data, keyToDataPath[key], EText(val));
  });

  return Expressions.setElementData(element, data);
};

type DeduplicateIdsInElementArgs = {
  element: ExpressionElement;
  componentMap: Component.ComponentMap;
  newIdMap: NewIdMapType;
};

const deduplicateIdsInElement = (
  arg: DeduplicateIdsInElementArgs
): {
  element: ExpressionElement;
  newIdMap: NewIdMapType;
} => {
  const {element, componentMap, newIdMap} = arg;
  const isInput = isFormInputElement(element);
  const isLabel = isFormLabel(element) || isFormFileUploadLabel(element);

  // Not a form input or label, move on...
  if (!isInput && !isLabel) {
    return arg;
  }

  const data = Expressions.getElementData(element);
  const attr = Expressions.getIn(data, [{in: 'Record', at: 'attr'}]);

  if (!attr) {
    return arg;
  }

  const oldId = Expressions.getText(
    Expressions.getIn(attr, [{in: 'Record', at: isLabel ? 'for' : 'id'}]) ||
      EText('')
  );

  // A label might not be attached to any input.
  if (!oldId) {
    return arg;
  }

  const idPrefix = trimLastNum(oldId);

  // The id and name attributes don’t match which means the user has
  // changed them. In this case we don’t want to touch them.
  const nameAttr = Expressions.getText(
    Expressions.getIn(attr, [{in: 'Record', at: 'name'}]) || EText('')
  );
  if (isInput && nameAttr !== null && !weakStringEqual(idPrefix, nameAttr)) {
    return arg;
  }

  const idLabelPair = has(oldId)(newIdMap)
    ? newIdMap[oldId]
    : getUniqueFormIdAndLabel(componentMap, idPrefix);

  // @ts-expect-error - TS2339 - Property 'id' does not exist on type '{ id: string; label: string; } | undefined'. | TS2339 - Property 'label' does not exist on type '{ id: string; label: string; } | undefined'.
  const {id, label} = idLabelPair;

  if (isLabel) {
    const newData = Expressions.setIn(data, ATTR_FOR_PATH, EText(id));

    return {
      element: Expressions.setElementData(element, newData),
      newIdMap: assoc(oldId)(idLabelPair)(newIdMap),
    };
  } else {
    let elementData = data;

    if (!isFormRadioInput(element)) {
      elementData = Expressions.setIn(elementData, ATTR_NAME_PATH, EText(id));
    }

    elementData = Expressions.setIn(elementData, FORM_NAME_PATH, EText(label));
    elementData = Expressions.setIn(elementData, ATTR_ID_PATH, EText(id));
    elementData = Expressions.setIn(
      elementData,
      ATTR_DATA_NAME_PATH,
      EText(label)
    );

    return {
      element: Expressions.setElementData(element, elementData),
      newIdMap: assoc(oldId)(idLabelPair)(newIdMap),
    };
  }
};

type DeduplicateIdsAccumulator = {
  elements: Array<ExpressionElement>;
  componentMap: Component.ComponentMap;
  newIdMap: NewIdMapType;
};

type DeduplicateIdsReducer = (
  result: DeduplicateIdsAccumulator,
  element: ExpressionElement
) => DeduplicateIdsAccumulator;

const deduplicateIdsReducer: DeduplicateIdsReducer = (result, element) => {
  const {
    element: newElement,
    newIdMap,
    componentMap,
  } = deduplicateIdsImpl({
    element,
    componentMap: result.componentMap,
    newIdMap: result.newIdMap,
  });

  return {
    elements: [...result.elements, newElement],
    componentMap,
    newIdMap,
  };
};

// Adds the new element to the body element, to ensure we can generate
// new IDs properly on each iteration.
const appendElementToBody = (
  componentMap: Component.ComponentMap,
  elemToAdd: ExpressionElement
): Component.ComponentMap => {
  const bodyElement = getBody(componentMap);
  if (!bodyElement) return componentMap;

  const bodyData = Expressions.getElementData(bodyElement);
  const nextBodyData = Expressions.updateIn(
    bodyData,
    CHILDREN_PATH,
    matchExpression({
      List: (children) => EList([...children, EElement(elemToAdd)]),
      _: identity,
    })
  );

  const nextBodyElem = Expressions.setElementData(bodyElement, nextBodyData);
  return updateComponentRenderInComponentMap(
    componentMap,
    pageComponentType,
    EElement(nextBodyElem)
  );
};

const deduplicateIdsImpl = (
  args: DeduplicateIdsInElementArgs
): DeduplicateIdsInElementArgs => {
  const {element, newIdMap: tempIdMap} = deduplicateIdsInElement(args);
  const elementData = Expressions.getElementData(element);
  const childrenElements = getChildren(element);

  if (!childrenElements) {
    return {
      element,
      componentMap: args.componentMap,
      newIdMap: tempIdMap,
    };
  }

  const {elements, componentMap, newIdMap} = childrenElements.reduce(
    deduplicateIdsReducer,
    {
      elements: emptyArray,
      componentMap:
        element === args.element
          ? args.componentMap
          : appendElementToBody(args.componentMap, element),
      newIdMap: tempIdMap,
    }
  );

  const newData = Expressions.setIn(
    elementData,
    CHILDREN_PATH,
    EList(elements.map(EElement))
  );

  return {
    element: Expressions.setElementData(element, newData),
    componentMap,
    newIdMap,
  };
};

export const deduplicateIds =
  (
    componentMap: Component.ComponentMap
  ): ((element: ExpressionElement) => ExpressionElement) =>
  (element: ExpressionElement) =>
    deduplicateIdsImpl({
      element,
      componentMap,
      newIdMap: emptyObject,
    }).element;

function setFormElementSource(
  componentMap: Component.ComponentMap,
  elementId: string,
  sourceExpr: Expression,
  componentName: Qualified<Component.ComponentName>
): Component.ComponentMap {
  const component = componentMap.get(componentName);

  const nextRender = component
    ? Expressions.updateElement(
        elementId,
        (element) => {
          const dataExpr = Expressions.getElementData(element);
          const nextDataExpr = Expressions.sparseSetIn(
            dataExpr,
            SOURCE_PATH,
            sourceExpr
          );

          return Expressions.setElementData(element, nextDataExpr);
        },
        Component.getRender(component)
      )
    : null;

  return nextRender
    ? updateComponentRenderInComponentMap(
        componentMap,
        componentName,
        nextRender
      )
    : componentMap;
}

export function setFormElementDefaultSource(
  componentMap: Component.ComponentMap,
  elementId: string,
  componentName: Qualified<Component.ComponentName>
): Component.ComponentMap {
  return setFormElementSource(
    componentMap,
    elementId,
    defaultSourceExpr,
    componentName
  );
}

export const resetFormElementsInTreeToDefaultSource: (
  arg1: Expression
) => Expression = Expressions.everywhere(
  matchExpression({
    Element: (el: ExpressionElement): Expression => {
      if (isFormForm(el)) {
        const dataExpr = Expressions.getElementData(el);
        const nextDataExpr = Expressions.sparseSetIn(
          dataExpr,
          SOURCE_PATH,
          defaultSourceExpr
        );
        return EElement(Expressions.setElementData(el, nextDataExpr));
      }
      return EElement(el);
    },
    _: (expr) => expr,
  })
);

const defaultSourceExpr = EUnion({
  tag: 'Default form',
  value: ERecord({}),
});
